import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Inbox, Calendar, DollarSign } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="border border-[#27272A] bg-[#0F0F0F] p-6 hover:border-[#FF331F]/50 transition-colors" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">{label}</div>
      <Icon className={`h-4 w-4 ${accent ? "text-[#FF331F]" : "text-zinc-600"}`} />
    </div>
    <div className="font-display uppercase text-4xl text-white">{value}</div>
  </div>
);

export default function Overview() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats/overview").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  return (
    <div className="p-8 md:p-12" data-testid="overview-page">
      <div className="mb-10">
        <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Trainer dashboard</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Today's snapshot.</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard icon={Calendar} label="Today's sessions" value={stats?.todays_sessions ?? "—"} accent testid="stat-today" />
        <StatCard icon={Users} label="Active clients" value={stats?.total_clients ?? "—"} testid="stat-clients" />
        <StatCard icon={Inbox} label="New leads" value={stats?.new_leads ?? "—"} testid="stat-leads" />
        <StatCard icon={DollarSign} label="Revenue" value={`$${(stats?.revenue ?? 0).toFixed(0)}`} accent testid="stat-revenue" />
      </div>

      <div className="border border-[#27272A] bg-[#0F0F0F]">
        <div className="border-b border-[#27272A] px-6 py-4 flex items-center justify-between">
          <div className="font-display uppercase tracking-widest text-sm">Upcoming sessions</div>
          <span className="text-xs text-zinc-500 uppercase tracking-widest">Next 5</span>
        </div>
        <div className="divide-y divide-[#27272A]" data-testid="upcoming-list">
          {(stats?.upcoming || []).length === 0 ? (
            <div className="p-10 text-center text-zinc-500 text-sm">No upcoming sessions scheduled.</div>
          ) : (
            stats.upcoming.map((b) => (
              <div key={b.id} className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-[#121212]" data-testid={`upcoming-${b.id}`}>
                <div className="flex items-center gap-4">
                  <div className="text-center border-r border-[#27272A] pr-4">
                    <div className="font-display text-3xl text-[#FF331F]">{new Date(b.starts_at).getDate()}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{new Date(b.starts_at).toLocaleString("en", { month: "short" })}</div>
                  </div>
                  <div>
                    <div className="font-display uppercase">{b.client_name}</div>
                    <div className="text-zinc-400 text-sm">{b.package_name}</div>
                    <div className="text-zinc-500 text-xs uppercase tracking-widest mt-1">
                      {new Date(b.starts_at).toLocaleString("en", { hour: "2-digit", minute: "2-digit" })} · {b.duration} min · {b.session_type}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${b.payment_status === "paid" ? "bg-emerald-500/10 text-emerald-400" : "bg-zinc-800 text-zinc-400"}`}>{b.payment_status}</span>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-[#FF331F]/10 text-[#FF331F]">{b.status}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
